import { makeObservable, override } from "mobx";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { OptimisticSyncUpdate, SyncModelKind } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { NoteSourceModelData } from "./types";
import { NoteSourceObservable } from "./NoteSourceObservable";
import { SyncUpdateValue } from "@/store/sync/types";
import { NoteSourceIndexes } from "@/store/sources/NoteSourceIndexes";
import { Maybe } from "@/domains/common/types";
import { AppStore } from "@/store/AppStore";
import { withDexieErrorHandling } from "@/domains/dexie-db/dbErrorHandling";
import Dexie from "dexie";
import { Uuid } from "@/domains/global/identifiers";

export class AppStoreNoteSourceStore extends BaseSyncModelStore<
  NoteSourceObservable,
  NoteSourceModelData
> {
  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.NoteSource, ...injectedDeps });
    makeObservable(this, {
      hasAnySourceForNote: true,
      createSyncModel: false,
      computeIndexes: override,
    });
  }

  createSyncModel(data: SyncUpdateValue<NoteSourceModelData>): NoteSourceObservable {
    return new NoteSourceObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<NoteSourceModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<NoteSourceModelData>[];
  }): Record<string, unknown> {
    return new NoteSourceIndexes({ remoteData, optimisticUpdates }).indexes;
  }

  public async hasAnySourceForNote({ noteId }: { noteId: Uuid }): Promise<boolean> {
    return withDexieErrorHandling(
      async () => {
        const count = await this.localTable
          .where("[note_id+source_id+model_id]")
          .between([noteId, Dexie.minKey, Dexie.minKey], [noteId, Dexie.maxKey, Dexie.maxKey])
          .count();

        return count > 0;
      },
      this.store,
      "[AppStoreNoteSourceStore] hasAnySourceForNote"
    );
  }
}
